'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useState, useEffect, useRef } from 'react'
import {
    Brain, Code2, Terminal, Zap, MessageSquare, Search, Cpu, Globe,
    Sparkles, ArrowRight, Wifi, Bot, Shield, Eye, GitBranch,
    Layers, Network, Radio, ChevronRight, FileCode, Monitor,
    Smartphone, Watch, Laptop, Mail, Hash, Command, Lock,
    RefreshCw, Activity, Send, CircleDot, Settings, Play
} from 'lucide-react'

// ============================================
// ANIMATED CODE TICKER
// ============================================

function CodeTicker() {
    const lines = [
        { prefix: '>>', text: 'search for auth logic in login.swift', color: '#a78bfa' },
        { prefix: '!>', text: 'sign up for T-Mobile prepaid plan', color: '#f472b6' },
        { prefix: '<>', text: 'goal Fix the WebSocket reconnect bug', color: '#4ade80' },
        { prefix: '>>', text: 'status', color: '#a78bfa' },
        { prefix: '<>', text: 'ask How does the A2A handshake work?', color: '#4ade80' },
        { prefix: '!>', text: 'book a flight to Miami next Friday', color: '#f472b6' },
        { prefix: '>>', text: 'diag', color: '#a78bfa' },
        { prefix: '<>', text: 'heartbeat', color: '#4ade80' },
    ]

    const [currentLine, setCurrentLine] = useState(0)
    const [displayText, setDisplayText] = useState('')
    const [isTyping, setIsTyping] = useState(true)

    useEffect(() => {
        const line = lines[currentLine]
        const fullText = `${line.prefix} ${line.text}`

        if (isTyping) {
            if (displayText.length < fullText.length) {
                const timeout = setTimeout(() => {
                    setDisplayText(fullText.substring(0, displayText.length + 1))
                }, 35 + Math.random() * 25)
                return () => clearTimeout(timeout)
            } else {
                const timeout = setTimeout(() => setIsTyping(false), 2000)
                return () => clearTimeout(timeout)
            }
        } else {
            setDisplayText('')
            setCurrentLine((prev) => (prev + 1) % lines.length)
            setIsTyping(true)
        }
    }, [displayText, isTyping, currentLine])

    const line = lines[currentLine]
    const prefixLen = line.prefix.length

    return (
        <div className="font-mono text-sm md:text-base">
            <span style={{ color: line.color }} className="font-bold">
                {displayText.substring(0, prefixLen)}
            </span>
            <span className="text-gray-300">
                {displayText.substring(prefixLen)}
            </span>
            <span className="animate-pulse text-[#4ade80]">▊</span>
        </div>
    )
}

// ============================================
// ANIMATED FLOW DIAGRAM
// ============================================

function RoutingDiagram() {
    const [activeNode, setActiveNode] = useState(0)

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveNode((prev) => (prev + 1) % 5)
        }, 2000)
        return () => clearInterval(interval)
    }, [])

    const nodes = [
        { label: 'WhatsApp', icon: MessageSquare, pos: 'left-[5%] top-[20%]' },
        { label: 'Slack', icon: Hash, pos: 'left-[5%] top-[50%]' },
        { label: 'Telegram', icon: Send, pos: 'left-[5%] top-[80%]' },
        { label: 'Router', icon: Network, pos: 'left-[38%] top-[50%]', isCenter: true },
        { label: 'Orchestrator', icon: Brain, pos: 'left-[72%] top-[50%]', isCenter: true },
    ]

    return (
        <div className="relative w-full h-64 md:h-80">
            {/* Connection lines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 300">
                {/* Platform → Router */}
                <line x1="100" y1="65" x2="260" y2="150" stroke="#4ade80" strokeWidth="1.5" strokeDasharray="6 4" opacity="0.4" />
                <line x1="100" y1="150" x2="260" y2="150" stroke="#4ade80" strokeWidth="1.5" strokeDasharray="6 4" opacity="0.4" />
                <line x1="100" y1="235" x2="260" y2="150" stroke="#4ade80" strokeWidth="1.5" strokeDasharray="6 4" opacity="0.4" />
                {/* Router → Orchestrator */}
                <line x1="340" y1="150" x2="460" y2="150" stroke="#a78bfa" strokeWidth="2" strokeDasharray="6 4" opacity="0.6" />
                {/* A2A label */}
                <text x="400" y="138" fill="#a78bfa" fontSize="11" textAnchor="middle" fontFamily="monospace">A2A</text>
                <text x="400" y="168" fill="#6b7280" fontSize="9" textAnchor="middle" fontFamily="monospace">WebSocket</text>
            </svg>

            {nodes.map((node, i) => (
                <div
                    key={node.label}
                    className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ${node.pos}`}
                >
                    <div className={`
                        flex flex-col items-center gap-1.5 p-3 rounded-xl border backdrop-blur-sm transition-all duration-300
                        ${activeNode === i
                            ? 'border-[#4ade80]/60 bg-[#4ade80]/10 shadow-lg shadow-[#4ade80]/20 scale-110'
                            : node.isCenter
                                ? 'border-purple-500/40 bg-purple-500/5'
                                : 'border-gray-700 bg-gray-900/50'
                        }
                    `}>
                        <node.icon className={`w-5 h-5 ${activeNode === i ? 'text-[#4ade80]'
                            : node.isCenter ? 'text-purple-400'
                                : 'text-gray-400'
                            }`} />
                        <span className={`text-xs font-medium ${activeNode === i ? 'text-[#4ade80]' : 'text-gray-400'
                            }`}>
                            {node.label}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    )
}

// ============================================
// LIVE PULSE INDICATOR
// ============================================

function PulseIndicator({ label, status }: { label: string; status: 'active' | 'idle' | 'waiting' }) {
    const colors = {
        active: 'bg-[#4ade80]',
        idle: 'bg-gray-500',
        waiting: 'bg-amber-400',
    }

    return (
        <div className="flex items-center gap-2">
            <div className="relative">
                <div className={`w-2.5 h-2.5 rounded-full ${colors[status]}`} />
                {status === 'active' && (
                    <div className={`absolute inset-0 w-2.5 h-2.5 rounded-full ${colors[status]} animate-ping`} />
                )}
            </div>
            <span className="text-xs text-gray-400 font-mono">{label}</span>
        </div>
    )
}

// ============================================
// HYPER SYMBOL CARD
// ============================================

function HyperSymbolCard({ symbol, label, description, color, examples }: {
    symbol: string
    label: string
    description: string
    color: string
    examples: string[]
}) {
    return (
        <div className="group bg-gray-900/60 border border-gray-800 rounded-2xl p-6 hover:border-gray-600 transition-all duration-300 hover:shadow-lg hover:shadow-black/20">
            <div className="flex items-start gap-4 mb-4">
                <div
                    className="flex items-center justify-center w-14 h-14 rounded-xl font-mono text-xl font-black tracking-tight"
                    style={{ backgroundColor: `${color}15`, color }}
                >
                    {symbol}
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-white">{label}</h3>
                    <p className="text-sm text-gray-400 mt-0.5">{description}</p>
                </div>
            </div>
            <div className="space-y-2">
                {examples.map((ex, i) => (
                    <div key={i} className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                        <span style={{ color }} className="font-bold">{symbol}</span> {ex}
                    </div>
                ))}
            </div>
        </div>
    )
}

// ============================================
// ARCHITECTURE LAYER CARD
// ============================================

function LayerCard({ title, subtitle, icon: Icon, color, items, protocol }: {
    title: string
    subtitle: string
    icon: any
    color: string
    items: string[]
    protocol: string
}) {
    return (
        <div className="relative bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-gray-600 transition-all group overflow-hidden">
            {/* Glow effect */}
            <div
                className="absolute -top-24 -right-24 w-48 h-48 rounded-full opacity-0 group-hover:opacity-10 transition-opacity duration-500 blur-3xl"
                style={{ backgroundColor: color }}
            />

            <div className="relative">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                            <Icon className="w-5 h-5" style={{ color }} />
                        </div>
                        <div>
                            <h3 className="text-white font-semibold">{title}</h3>
                            <p className="text-xs text-gray-500">{subtitle}</p>
                        </div>
                    </div>
                    <span className="text-xs font-mono px-2 py-1 rounded-md bg-gray-800 text-gray-400">{protocol}</span>
                </div>
                <ul className="space-y-2">
                    {items.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                            <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
                            {item}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}

// ============================================
// MAIN PAGE
// ============================================

export default function VibeCodingPage() {
    return (
        <div className="min-h-screen bg-gray-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link href="/" className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={40}
                                height={40}
                                className="rounded-xl"
                            />
                            <span className="text-xl font-bold">
                                <span className="text-[#4ade80]">Link</span>
                                <span className="text-white">Box AI</span>
                            </span>
                        </Link>

                        <div className="hidden md:flex items-center gap-8">
                            <Link href="/features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</Link>
                            <Link href="/vibe-coding" className="text-[#4ade80] text-sm font-medium">Vibe Coding</Link>
                            <Link href="/ecosystem" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Ecosystem</Link>
                            <Link href="/orchestrator" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Orchestrator</Link>
                            <Link href="/hardware" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Hardware</Link>
                            <Link href="/robotics" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Robotics</Link>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link href="/login" className="text-gray-300 hover:text-white transition-colors text-sm font-medium px-4 py-2">
                                Log In
                            </Link>
                            <Link
                                href="/signup"
                                className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-5 py-2 rounded-lg text-sm font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25"
                            >
                                Get Started Free
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* ═══════════════════════════════════ */}
            {/* HERO SECTION */}
            {/* ═══════════════════════════════════ */}
            <section className="relative pt-32 pb-24 overflow-hidden">
                {/* Background layers */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a0f1a] via-gray-950 to-[#0d1117]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(168,139,250,0.08),transparent_50%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,rgba(74,222,128,0.06),transparent_50%)]" />

                {/* Grid pattern */}
                <div className="absolute inset-0 opacity-[0.03]" style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                    backgroundSize: '60px 60px'
                }} />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            {/* Badge */}
                            <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-8">
                                <Activity className="w-4 h-4 text-purple-400" />
                                <span className="text-purple-400 text-sm font-medium">Agent-to-Agent Protocol</span>
                            </div>

                            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.05]">
                                <span className="text-white">Vibe </span>
                                <span className="bg-gradient-to-r from-[#a78bfa] via-[#c084fc] to-[#e879f9] bg-clip-text text-transparent">Coding</span>
                                <br />
                                <span className="text-white text-4xl md:text-5xl lg:text-5xl">from </span>
                                <span className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] bg-clip-text text-transparent text-4xl md:text-5xl lg:text-5xl">Anywhere</span>
                            </h1>

                            <p className="text-xl text-gray-400 max-w-xl mb-8 leading-relaxed">
                                Send a WhatsApp message. Your Mac starts coding. The agent asks questions.
                                You answer from bed. Ship features in your pajamas.
                            </p>

                            {/* Status indicators */}
                            <div className="flex flex-wrap items-center gap-6 mb-10">
                                <PulseIndicator label="Sovereign Brain" status="active" />
                                <PulseIndicator label="A2A Server" status="active" />
                                <PulseIndicator label="Intel Layer" status="active" />
                            </div>

                            <div className="flex flex-wrap gap-4">
                                <Link
                                    href="/link-code"
                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#a78bfa] to-[#c084fc] text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                                >
                                    <Code2 className="w-5 h-5" />
                                    Open Link Code Pro
                                </Link>
                                <Link
                                    href="/orchestrator/setup"
                                    className="inline-flex items-center gap-2 border border-gray-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
                                >
                                    <Monitor className="w-5 h-5" />
                                    Setup Guide
                                </Link>
                            </div>
                        </div>

                        {/* Code ticker terminal */}
                        <div className="relative">
                            <div className="bg-[#0d1117] border border-gray-800 rounded-2xl overflow-hidden shadow-2xl shadow-black/50">
                                {/* Terminal header */}
                                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800 bg-gray-900/50">
                                    <div className="flex gap-1.5">
                                        <div className="w-3 h-3 rounded-full bg-red-500/60" />
                                        <div className="w-3 h-3 rounded-full bg-amber-500/60" />
                                        <div className="w-3 h-3 rounded-full bg-green-500/60" />
                                    </div>
                                    <span className="text-xs text-gray-500 ml-2 font-mono">WhatsApp → Orchestrator</span>
                                    <div className="ml-auto flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-[#4ade80] animate-pulse" />
                                        <span className="text-xs text-gray-500 font-mono">live</span>
                                    </div>
                                </div>

                                {/* Terminal body */}
                                <div className="p-6 min-h-[200px] flex flex-col justify-between">
                                    <div className="space-y-3">
                                        <div className="text-xs text-gray-600 font-mono">
                                            $ linkbox-orchestrator --port 8080 --a2a 8081
                                        </div>
                                        <div className="text-xs text-gray-500 font-mono">
                                            🧠 Sovereign Brain online | 💓 Heartbeat active
                                        </div>
                                        <div className="text-xs text-gray-500 font-mono">
                                            🌐 A2A WebSocket ready on :8081
                                        </div>
                                        <div className="h-px bg-gray-800 my-3" />
                                        <div className="text-xs text-gray-500 font-mono mb-2">
                                            📨 Incoming message from WhatsApp:
                                        </div>
                                        <CodeTicker />
                                    </div>
                                </div>
                            </div>

                            {/* Floating badges */}
                            <div className="absolute -top-4 -right-4 bg-gray-900 border border-purple-500/30 rounded-xl px-3 py-1.5 text-xs font-mono text-purple-400 shadow-lg">
                                A2A :8081
                            </div>
                            <div className="absolute -bottom-4 -left-4 bg-gray-900 border border-amber-500/30 rounded-xl px-3 py-1.5 text-xs font-mono text-amber-400 shadow-lg">
                                MCP (internal tools)
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════ */}
            {/* HOW IT WORKS — ROUTING FLOW */}
            {/* ═══════════════════════════════════ */}
            <section className="relative py-24 border-t border-gray-800/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-full px-4 py-2 mb-6">
                            <Network className="w-4 h-4 text-[#4ade80]" />
                            <span className="text-[#4ade80] text-sm font-medium">Message Routing</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            How a Message <span className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] bg-clip-text text-transparent">Becomes Code</span>
                        </h2>
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                            Every message from every platform hits a single front door. From there, the router
                            decides: cloud AI, local Orchestrator, or full agentic coding session.
                        </p>
                    </div>

                    {/* Animated routing diagram */}
                    <div className="mb-20">
                        <RoutingDiagram />
                    </div>

                    {/* Step-by-step flow */}
                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            {
                                step: '01',
                                title: 'Message Arrives',
                                desc: 'You send a message from WhatsApp, Slack, Telegram, Discord, or SMS.',
                                icon: MessageSquare,
                                color: '#60a5fa'
                            },
                            {
                                step: '02',
                                title: 'Intent Classified',
                                desc: 'The intelligent-message-router classifies your intent against the skill manifest.',
                                icon: Brain,
                                color: '#a78bfa'
                            },
                            {
                                step: '03',
                                title: 'Route Decided',
                                desc: 'Orchestrator tasks → A2A WebSocket. General queries → Cloud AI. Everything agent goes through A2A.',
                                icon: Network,
                                color: '#f472b6'
                            },
                            {
                                step: '04',
                                title: 'Agent Executes',
                                desc: 'Your Desktop Orchestrator receives the task, reasons, acts, and reports back.',
                                icon: Cpu,
                                color: '#4ade80'
                            },
                        ].map((step) => (
                            <div key={step.step} className="relative">
                                <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 h-full hover:border-gray-600 transition-colors">
                                    <div className="text-3xl font-black mb-4" style={{ color: step.color }}>
                                        {step.step}
                                    </div>
                                    <div className="flex items-center gap-3 mb-3">
                                        <step.icon className="w-5 h-5" style={{ color: step.color }} />
                                        <h3 className="text-white font-semibold">{step.title}</h3>
                                    </div>
                                    <p className="text-sm text-gray-400">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════ */}
            {/* HYPER SYMBOLS — COMMAND PREFIX SYSTEM */}
            {/* ═══════════════════════════════════ */}
            <section className="relative py-24 border-t border-gray-800/50">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(168,139,250,0.05),transparent_60%)]" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
                            <Command className="w-4 h-4 text-purple-400" />
                            <span className="text-purple-400 text-sm font-medium">Hyper Symbols</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Speak in <span className="bg-gradient-to-r from-[#a78bfa] to-[#e879f9] bg-clip-text text-transparent">Symbols</span>
                        </h2>
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                            Prefix your message with a hyper symbol for instant, deterministic routing.
                            No AI classification needed. No ambiguity. Just precision.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        <HyperSymbolCard
                            symbol=">>"
                            label="Sovereign Brain"
                            description="Route directly to your IDE's coding brain via A2A protocol"
                            color="#a78bfa"
                            examples={[
                                'search for auth logic',
                                'status',
                                'ask How does the WebSocket reconnect?',
                                'diag',
                            ]}
                        />
                        <HyperSymbolCard
                            symbol="!>"
                            label="Browser Automation"
                            description="Dispatch a task requiring browser interaction via Computer Use"
                            color="#f472b6"
                            examples={[
                                'sign up for T-Mobile prepaid',
                                'book a flight to Miami',
                                'order groceries from Instacart',
                            ]}
                        />
                        <HyperSymbolCard
                            symbol="<>"
                            label="IDE Commands"
                            description="Direct coding session control — goals, files, heartbeats"
                            color="#4ade80"
                            examples={[
                                'goal Fix the login timeout bug',
                                'open src/auth/login.swift',
                                'heartbeat',
                                'answer Yes, use production DB',
                            ]}
                        />
                    </div>

                    {/* No-prefix explanation */}
                    <div className="mt-10 bg-gray-900/30 border border-gray-800 rounded-2xl p-6 max-w-3xl mx-auto">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0">
                                <Sparkles className="w-5 h-5 text-gray-400" />
                            </div>
                            <div>
                                <h4 className="text-white font-semibold mb-1">No prefix? AI figures it out.</h4>
                                <p className="text-sm text-gray-400">
                                    Messages without a hyper symbol are classified by GPT against the skill manifest
                                    (60+ intents). This works great for natural conversation, but symbols give you
                                    instant, deterministic control when you know exactly what you want.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════ */}
            {/* ARCHITECTURE LAYERS */}
            {/* ═══════════════════════════════════ */}
            <section className="relative py-24 border-t border-gray-800/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2 mb-6">
                            <Layers className="w-4 h-4 text-cyan-400" />
                            <span className="text-cyan-400 text-sm font-medium">Architecture</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Five Layers of <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Intelligence</span>
                        </h2>
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                            The Orchestrator isn't one monolith — it's a stack of specialized layers,
                            each doing one thing exceptionally well.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <LayerCard
                            title="Sovereign Brain"
                            subtitle="Coding Session Manager"
                            icon={Brain}
                            color="#a78bfa"
                            protocol="A2A layer"
                            items={[
                                'Multi-turn conversation thread',
                                'Autonomous task heartbeat (5s pulse)',
                                'Task phase management (plan → code → test)',
                                'User question/answer dialogue',
                                'Stall detection (30s timeout)',
                            ]}
                        />
                        <LayerCard
                            title="A2A Server"
                            subtitle="Agent-to-Agent Protocol"
                            icon={Radio}
                            color="#e879f9"
                            protocol="port 8081"
                            items={[
                                'WebSocket bidirectional dialogue',
                                'Task request / accept / complete lifecycle',
                                'Safety confirmations for risky actions',
                                'Progress updates streamed in real-time',
                                'Question/answer with timeout handling',
                            ]}
                        />
                        <LayerCard
                            title="Intel Layer"
                            subtitle="Code Intelligence"
                            icon={Search}
                            color="#4ade80"
                            protocol="A2A + REST"
                            items={[
                                'Semantic vector search across codebase',
                                'Real-time diagnostics (errors, warnings)',
                                'LLM-powered code completion',
                                'Context pruning for token budgets',
                                'AST-aware repo map generation',
                            ]}
                        />
                        <LayerCard
                            title="Omni-Channel Gateway"
                            subtitle="Universal Platform Bridge"
                            icon={Globe}
                            color="#60a5fa"
                            protocol="Edge Function"
                            items={[
                                'WhatsApp, Slack, Telegram, Discord, SMS',
                                'Alexa, Google Home, LinkBox Voice voice agents',
                                'Hyper-symbol prefix routing (>>, !>, <>)',
                                'All agent tasks routed via A2A — one artery',
                                'Falls back to cloud AI when Orchestrator offline',
                            ]}
                        />
                        <LayerCard
                            title="BennyBoy Engine"
                            subtitle="Browser Automation"
                            icon={Globe}
                            color="#f472b6"
                            protocol="Computer Use"
                            items={[
                                'Google AI Computer Use via Vertex AI',
                                'High-fidelity CDP browser control',
                                'Multi-step agentic web tasks',
                                'Screenshot reasoning loop',
                                'Google account pre-selection',
                            ]}
                        />
                        <LayerCard
                            title="MCP Server"
                            subtitle="Model Context Protocol"
                            icon={Settings}
                            color="#fbbf24"
                            protocol="IPC"
                            items={[
                                'File system operations (read/write/search)',
                                'Terminal command execution',
                                'Memory store (remember/recall)',
                                'Entry search via RAG',
                                'Git operations & GitHub integration',
                            ]}
                        />
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════ */}
            {/* TWO ARTERIES — A2A + MCP */}
            {/* ═══════════════════════════════════ */}
            <section className="relative py-24 border-t border-gray-800/50">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_50%,rgba(74,222,128,0.04),transparent_60%)]" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Two Arteries, <span className="bg-gradient-to-r from-[#a78bfa] to-[#fbbf24] bg-clip-text text-transparent">One Brain</span>
                        </h2>
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                            Not 1,000 small connections — two clean, purpose-built communication channels.
                            A2A for every agent conversation. MCP for every tool action.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {/* A2A */}
                        <div className="bg-gradient-to-br from-purple-500/5 to-transparent border border-purple-500/20 rounded-2xl p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                                    <Radio className="w-6 h-6 text-purple-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">A2A — The External Artery</h3>
                                    <span className="text-xs font-mono text-purple-400">port 8081 • WebSocket • bidirectional</span>
                                </div>
                            </div>
                            <p className="text-gray-400 mb-4 text-sm leading-relaxed">
                                <strong className="text-white">Every agent speaks A2A.</strong>{' '}
                                WhatsApp, Slack, Alexa, Google Home, LinkBox Voice, the web IDE — they all connect
                                to the Orchestrator through one universal WebSocket protocol. Tasks, questions,
                                progress, safety checks — all flow through this single artery.
                            </p>
                            <div className="text-xs text-gray-500 mb-4 font-medium uppercase tracking-wider">Message Lifecycle</div>
                            <div className="space-y-2">
                                <div className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                                    <span className="text-purple-400">→</span> a2a_task_request <span className="text-gray-600">// "Fix the auth bug"</span>
                                </div>
                                <div className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                                    <span className="text-yellow-400">←</span> a2a_progress <span className="text-gray-600">// 40% "Analyzing codebase..."</span>
                                </div>
                                <div className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                                    <span className="text-yellow-400">←</span> a2a_question <span className="text-gray-600">// "Which database?"</span>
                                </div>
                                <div className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                                    <span className="text-purple-400">→</span> a2a_user_response <span className="text-gray-600">// "Use production DB"</span>
                                </div>
                                <div className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                                    <span className="text-[#4ade80]">←</span> a2a_complete <span className="text-gray-600">// "Bug fixed, 3 files changed"</span>
                                </div>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {['WhatsApp', 'Slack', 'Alexa', 'Google Home', 'LinkBox Voice', 'Web IDE'].map(p => (
                                    <span key={p} className="text-xs font-mono px-2 py-1 rounded-md bg-purple-500/10 text-purple-300 border border-purple-500/20">{p}</span>
                                ))}
                            </div>
                        </div>

                        {/* MCP */}
                        <div className="bg-gradient-to-br from-amber-500/5 to-transparent border border-amber-500/20 rounded-2xl p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                                    <Settings className="w-6 h-6 text-amber-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">MCP — The Internal Artery</h3>
                                    <span className="text-xs font-mono text-amber-400">Model Context Protocol • tool execution</span>
                                </div>
                            </div>
                            <p className="text-gray-400 mb-4 text-sm leading-relaxed">
                                <strong className="text-white">Agents don't touch tools directly.</strong>{' '}
                                When the Orchestrator needs to read a file, run a terminal command, search your
                                knowledge base, or push to GitHub — it invokes MCP tools internally. This keeps
                                the tool layer isolated, secure, and composable.
                            </p>
                            <div className="text-xs text-gray-500 mb-4 font-medium uppercase tracking-wider">Available Tools</div>
                            <div className="space-y-2">
                                <div className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                                    <span className="text-amber-400">⚙</span> read_file / write_file / list_directory
                                </div>
                                <div className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                                    <span className="text-amber-400">⚙</span> run_terminal_command
                                </div>
                                <div className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                                    <span className="text-amber-400">⚙</span> search_entries (RAG knowledge base)
                                </div>
                                <div className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                                    <span className="text-amber-400">⚙</span> remember / recall (persistent memory)
                                </div>
                                <div className="font-mono text-xs bg-gray-950/60 rounded-lg px-3 py-2 text-gray-300 border border-gray-800/50">
                                    <span className="text-amber-400">⚙</span> git_status / git_commit / github_pr
                                </div>
                            </div>
                            <div className="mt-4 bg-gray-950/40 rounded-xl p-3 border border-gray-800/50">
                                <p className="text-xs text-gray-500">
                                    <span className="text-amber-400 font-semibold">Why not HTTP?</span>{' '}
                                    HTTP (port 8080) exists only for the Link Code Pro web IDE bridge — a user-facing
                                    UI, not an agent. All agent-to-Orchestrator communication goes through A2A.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Architecture principle callout */}
                    <div className="mt-12 max-w-3xl mx-auto bg-gray-900/30 border border-gray-800 rounded-2xl p-6">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                                <Shield className="w-5 h-5 text-purple-400" />
                            </div>
                            <div>
                                <h4 className="text-white font-semibold mb-1">Design Principle: Two arteries, not 1,000 connections</h4>
                                <p className="text-sm text-gray-400">
                                    Instead of building separate HTTP endpoints for every feature (status, search,
                                    diagnostics, goals, files...), we funnel everything through two clean channels.
                                    A2A handles the conversation. MCP handles the tools. No fragmentation. No confusion.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════ */}
            {/* LINK CODE PRO IDE */}
            {/* ═══════════════════════════════════ */}
            <section className="relative py-24 border-t border-gray-800/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-full px-4 py-2 mb-6">
                            <Code2 className="w-4 h-4 text-[#4ade80]" />
                            <span className="text-[#4ade80] text-sm font-medium">Link Code Pro</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            A Full IDE in Your <span className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] bg-clip-text text-transparent">Browser</span>
                        </h2>
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                            Monaco editor. File tree. Terminal. Git. AI chat with 5 modes.
                            All connected to your Mac via the Orchestrator bridge.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { icon: FileCode, title: 'Monaco Editor', desc: 'Full VS Code editing experience with syntax highlighting, IntelliSense, and multi-tab support' },
                            { icon: Terminal, title: 'Integrated Terminal', desc: 'Run shell commands on your Mac remotely via the Orchestrator bridge' },
                            { icon: GitBranch, title: 'Git Integration', desc: 'Stage, commit, push, diff — full version control without leaving the browser' },
                            { icon: Bot, title: 'AI Chat (5 Modes)', desc: 'Code, Architect, Ask, Debug, Edit — each tuned for a different workflow' },
                            { icon: Search, title: 'Semantic Code Search', desc: 'Vector-powered search that understands meaning, not just keywords' },
                            { icon: Shield, title: 'Self-Healing Loop', desc: 'AI auto-fixes diagnostics: generates patch → applies → re-checks until clean' },
                            { icon: Eye, title: 'Context Pruning', desc: 'Token-aware context window: target file + imports + semantic chunks + repo map' },
                            { icon: Sparkles, title: 'AI Code Completion', desc: 'Ghost text completions powered by your chosen LLM provider' },
                            { icon: Activity, title: 'Live Diagnostics', desc: 'Real-time error and warning feed from the Orchestrator Intel Layer' },
                        ].map((feature) => (
                            <div
                                key={feature.title}
                                className="group bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-[#4ade80]/40 transition-all"
                            >
                                <div className="w-10 h-10 rounded-xl bg-[#4ade80]/10 flex items-center justify-center mb-4 group-hover:bg-[#4ade80]/20 transition-colors">
                                    <feature.icon className="w-5 h-5 text-[#4ade80]" />
                                </div>
                                <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                                <p className="text-sm text-gray-400">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════ */}
            {/* SUPPORTED PLATFORMS */}
            {/* ═══════════════════════════════════ */}
            <section className="relative py-24 border-t border-gray-800/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Code from <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Everywhere</span>
                        </h2>
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                            Every messaging platform becomes a coding terminal.
                            Same commands, same brain, same results.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {[
                            { name: 'WhatsApp', icon: MessageSquare, color: '#25D366' },
                            { name: 'Slack', icon: Hash, color: '#4A154B' },
                            { name: 'Telegram', icon: Send, color: '#0088cc' },
                            { name: 'Discord', icon: MessageSquare, color: '#5865F2' },
                            { name: 'SMS', icon: Smartphone, color: '#60a5fa' },
                        ].map((platform) => (
                            <div
                                key={platform.name}
                                className="flex flex-col items-center gap-3 bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-gray-600 transition-colors group"
                            >
                                <div
                                    className="w-12 h-12 rounded-xl flex items-center justify-center transition-all group-hover:scale-110"
                                    style={{ backgroundColor: `${platform.color}15` }}
                                >
                                    <platform.icon className="w-6 h-6" style={{ color: platform.color }} />
                                </div>
                                <span className="text-sm font-medium text-gray-300">{platform.name}</span>
                            </div>
                        ))}
                    </div>

                    {/* Voice Agents */}
                    <div className="mt-8">
                        <p className="text-center text-sm text-gray-500 mb-4 font-medium uppercase tracking-wider">Voice Agents — All via A2A</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                            {[
                                { name: 'Amazon Alexa', icon: Bot, color: '#00CAFF' },
                                { name: 'Google Home', icon: Globe, color: '#4285F4' },
                                { name: 'LinkBox Voice', icon: Sparkles, color: '#e879f9' },
                                { name: 'Link Code Pro', icon: Monitor, color: '#4ade80' },
                            ].map((voice) => (
                                <div
                                    key={voice.name}
                                    className="flex flex-col items-center gap-3 bg-gray-900/50 border border-gray-800 rounded-2xl p-5 hover:border-gray-600 transition-colors group"
                                >
                                    <div
                                        className="w-10 h-10 rounded-xl flex items-center justify-center transition-all group-hover:scale-110"
                                        style={{ backgroundColor: `${voice.color}15` }}
                                    >
                                        <voice.icon className="w-5 h-5" style={{ color: voice.color }} />
                                    </div>
                                    <span className="text-xs font-medium text-gray-300">{voice.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════ */}
            {/* CLOUD CONTEXT & BYOS */}
            {/* ═══════════════════════════════════ */}
            <section className="relative py-24 border-t border-gray-800/50">
                <div className="absolute inset-0 bg-[#0d1117] bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.05),transparent_50%)]" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-[#38bdf8]/10 border border-[#38bdf8]/30 rounded-full px-4 py-2 mb-6">
                            <Wifi className="w-4 h-4 text-[#38bdf8]" />
                            <span className="text-[#38bdf8] text-sm font-medium">Bring Your Own Storage (BYOS)</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Orchestrator Offline? <span className="bg-gradient-to-r from-[#38bdf8] to-[#818cf8] bg-clip-text text-transparent">No Problem.</span>
                        </h2>
                        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                            When your Mac/PC Queen Bee Orchestrator is unreachable, your multi-device context doesn't die.
                            Our BYOS fallback seamlessly pulls your latest session memory from your connected cloud storage.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-[#38bdf8]/40 transition-all group">
                            <div className="w-12 h-12 rounded-xl bg-[#38bdf8]/10 flex items-center justify-center mb-4 group-hover:bg-[#38bdf8]/20 transition-colors">
                                <Monitor className="w-6 h-6 text-[#38bdf8]" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-3">1. Continuous Sync</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                While running, your desktop Orchestrator maintains a lightweight JSON "10-Byte Ping" backup of your active context window to your Google Drive or Dropbox.
                            </p>
                        </div>
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-[#818cf8]/40 transition-all group">
                            <div className="w-12 h-12 rounded-xl bg-[#818cf8]/10 flex items-center justify-center mb-4 group-hover:bg-[#818cf8]/20 transition-colors">
                                <Wifi className="w-6 h-6 text-[#818cf8]" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-3">2. Mobile Fallback</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                If you open the LinkBox app or Web IDE while your PC is asleep, the router detects the connection failure and prompts you to pull the cloud state.
                            </p>
                        </div>
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-[#c084fc]/40 transition-all group">
                            <div className="w-12 h-12 rounded-xl bg-[#c084fc]/10 flex items-center justify-center mb-4 group-hover:bg-[#c084fc]/20 transition-colors">
                                <Smartphone className="w-6 h-6 text-[#c084fc]" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-3">3. Local Independence</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Once pulled, your device becomes its own independent sovereign brain—handling context and edits entirely on the frontend or mobile app until the Orchestrator reconnects.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════ */}
            {/* CTA */}
            {/* ═══════════════════════════════════ */}
            <section className="relative py-24 border-t border-gray-800/50">
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/30 to-transparent" />
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Vibe Code?
                    </h2>
                    <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
                        Install the Orchestrator on your Mac. Connect your messaging apps.
                        Start shipping from anywhere.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            href="/orchestrator/setup"
                            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#a78bfa] to-[#c084fc] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all text-lg"
                        >
                            <Monitor className="w-5 h-5" />
                            Setup Orchestrator
                        </Link>
                        <Link
                            href="/link-code"
                            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-8 py-4 rounded-xl font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25 text-lg"
                        >
                            <Code2 className="w-5 h-5" />
                            Open Link Code Pro
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-800 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={32}
                                height={32}
                                className="rounded-lg"
                            />
                            <span className="text-gray-400 text-sm">© 2026 BB23 LLC. All rights reserved.</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <Link href="/features" className="text-gray-400 hover:text-white text-sm transition-colors">Features</Link>
                            <Link href="/orchestrator" className="text-gray-400 hover:text-white text-sm transition-colors">Orchestrator</Link>
                            <Link href="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy</Link>
                            <Link href="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">Terms</Link>
                            <Link href="https://bb23llc.com" className="text-gray-400 hover:text-white text-sm transition-colors">BB23 LLC</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    )
}
